import { db } from '@/src/renderer/lib/db/indexeddb'

export interface ForeignDataReport {
  hasForeignData: boolean
  counts: {
    projects: number
    tasks: number
    sections: number
    tags: number
    goals: number
  }
  /** Distinct foreign userIds encountered (for logging / telemetry) */
  foreignUserIds: string[]
}

/**
 * Scan all Dexie tables with a userId column and report any records whose
 * userId is neither null (offline) nor equal to the currently logged-in user.
 *
 * Offline records (userId === null) are intentionally NOT treated as foreign —
 * they represent the offline-first flow where ownership is established on
 * first sync to the cloud.
 *
 * Used as a pre-sync guard in the sync engine. If this returns
 * hasForeignData=true, Dexie is in a poisoned state from an unclean account
 * switch and must be wiped before any sync operation runs, otherwise the
 * reconciler could rewrite the foreign records under the current user's id.
 */
export async function detectForeignLocalData(currentUserId: string): Promise<ForeignDataReport> {
  const foreignIds = new Set<string>()
  const counts = { projects: 0, tasks: 0, sections: 0, tags: 0, goals: 0 }

  type TableName = keyof typeof counts
  const tables: Array<[TableName, typeof db.projects | typeof db.tasks | typeof db.sections | typeof db.tags | typeof db.goals]> = [
    ['projects', db.projects],
    ['tasks', db.tasks],
    ['sections', db.sections],
    ['tags', db.tags],
    ['goals', db.goals],
  ]

  for (const [name, table] of tables) {
    const foreign = await table
      .filter((r: { userId?: string | null }) => r.userId != null && r.userId !== currentUserId)
      .toArray()
    counts[name] = foreign.length
    for (const r of foreign) {
      const uid = (r as { userId?: string | null }).userId
      if (uid) foreignIds.add(uid)
    }
  }

  const hasForeignData =
    counts.projects > 0 || counts.tasks > 0 || counts.sections > 0 || counts.tags > 0 || counts.goals > 0

  return {
    hasForeignData,
    counts,
    foreignUserIds: Array.from(foreignIds),
  }
}
